import { SKY_VESPER } from '../../../packages/domain/src/125-sky-vesper.mjs';

const SEGMENTS = 64,
  RING_VERTICES = SKY_VESPER.ringLimit * SEGMENTS * 6,
  PARTICLES = 64;
/** Source-driven pressure wave arcs leave the exact domain quiet gap open.
 * Reduced motion retains every collision cue, hiding only nonphysical trails.
 * All arrays/materials are fixed allocations, owned by the scene resource owner. */
export function createSkyVesperEffects(T, scene, own, reduced = () => false) {
  const geometry = own(new T.BufferGeometry());
  const position = new T.Float32BufferAttribute(new Float32Array(RING_VERTICES * 3), 3),
    uv = new T.Float32BufferAttribute(new Float32Array(RING_VERTICES * 2), 2);
  position.setUsage(T.DynamicDrawUsage);
  uv.setUsage(T.DynamicDrawUsage);
  geometry.setAttribute('position', position);
  geometry.setAttribute('uv', uv);
  geometry.setDrawRange(0, 0);
  const material = own(
    new T.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      side: T.DoubleSide,
      blending: T.AdditiveBlending,
      vertexShader: `varying vec2 ringUv;void main(){ringUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
      fragmentShader:
        `varying vec2 ringUv;void main(){float edge=1.0-abs(ringUv.x*2.0-1.0);float core=pow(edge,2.0);vec3 color=mix(vec3(.08,.4,.85),vec3(.7,.96,1.0),core);gl_FragColor=vec4(color,(.3+.6*core)*ringUv.y);#include <tonemapping_fragment>\n#include <colorspace_fragment>}`.replace(
          ';#include',
          ';\n#include',
        ),
    }),
  );
  const mesh = new T.Mesh(geometry, material);
  mesh.name = 'Vesper pressure rings with physical quiet gaps';
  mesh.frustumCulled = false;
  mesh.visible = false;
  scene.add(mesh);
  const particles = own(new T.BufferGeometry()),
    particlePosition = new T.Float32BufferAttribute(new Float32Array(PARTICLES * 3), 3),
    color = new T.Float32BufferAttribute(new Float32Array(PARTICLES * 3), 3);
  particlePosition.setUsage(T.DynamicDrawUsage);
  color.setUsage(T.DynamicDrawUsage);
  particles.setAttribute('position', particlePosition);
  particles.setAttribute('color', color);
  particles.setDrawRange(0, 0);
  const pointMaterial = own(
    new T.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: T.AdditiveBlending,
      vertexColors: true,
      vertexShader: `varying vec3 pulseColor;void main(){pulseColor=color;vec4 view=modelViewMatrix*vec4(position,1.0);gl_PointSize=clamp(260.0/max(1.0,-view.z),2.0,38.0);gl_Position=projectionMatrix*view;}`,
      fragmentShader:
        `varying vec3 pulseColor;void main(){float r=length(gl_PointCoord*2.0-1.0);if(r>1.0)discard;gl_FragColor=vec4(pulseColor,pow(1.0-r,1.5));#include <tonemapping_fragment>\n#include <colorspace_fragment>}`.replace(
          ';#include',
          ';\n#include',
        ),
    }),
  );
  const points = new T.Points(particles, pointMaterial);
  points.name = 'Vesper locked pulse and crystal return';
  points.frustumCulled = false;
  points.visible = false;
  scene.add(points);
  let ringCount = 0,
    particleCount = 0,
    vertices = 0;
  function addPoint(p, reflected) {
    if (particleCount >= PARTICLES) return;
    particlePosition.setXYZ(particleCount, p.x, p.y, p.z);
    color.setXYZ(particleCount, reflected ? 0.9 : 0.35, reflected ? 0.75 : 0.85, 1);
    particleCount++;
  }
  function ring(r) {
    const start = r.gap + SKY_VESPER.gapHalfAngle,
      sweep = Math.PI * 2 - SKY_VESPER.gapHalfAngle * 2,
      half = SKY_VESPER.ringWidth / 2;
    for (let i = 0; i < SEGMENTS; i++) {
      const angles = [start + (sweep * i) / SEGMENTS, start + (sweep * (i + 1)) / SEGMENTS];
      const corners = [
        [0, -1],
        [0, 1],
        [1, 1],
        [0, -1],
        [1, 1],
        [1, -1],
      ];
      for (const [end, side] of corners) {
        const a = angles[end],
          radius = r.radius + side * half;
        position.setXYZ(vertices, r.x + Math.cos(a) * radius, r.y + Math.sin(a) * radius, r.z);
        uv.setXY(vertices, (side + 1) / 2, Math.max(0.3, 1 - r.age / 7));
        vertices++;
      }
    }
    ringCount++;
  }
  return {
    draw(state, visible = true) {
      ringCount = particleCount = vertices = 0;
      const boss = state?.boss,
        v =
          boss?.vesper ??
          (state.vesperLesson?.active && state.vesperLesson.sector === state.sector
            ? state.vesperLesson
            : null);
      if (visible && v && !boss?.defeated) {
        const quiet = reduced();
        for (const r of v.rings.slice(0, SKY_VESPER.ringLimit)) ring(r);
        for (const pulse of v.pulses.slice(0, SKY_VESPER.pulseLimit)) {
          addPoint(pulse.head, pulse.mode === 'reflected');
          if (!quiet)
            for (let i = 1; i <= 8; i++) {
              const t = Math.max(0, 1 - i * 0.025);
              addPoint(
                {
                  x: pulse.from.x + (pulse.head.x - pulse.from.x) * t,
                  y: pulse.from.y + (pulse.head.y - pulse.from.y) * t,
                  z: pulse.from.z + (pulse.head.z - pulse.from.z) * t,
                },
                pulse.mode === 'reflected',
              );
            }
        }
        if (v.phase === 'track') {
          const origin = boss?.authoredPose.sockets.sonic_emitter ?? v.emitter,
            target = { x: state.x, y: state.y, z: state.pilotZ ?? 1 };
          for (let i = 0; i < 16; i++) {
            const t = i / 15;
            addPoint(
              {
                x: origin.x + (target.x - origin.x) * t,
                y: origin.y + (target.y - origin.y) * t,
                z: origin.z + (target.z - origin.z) * t,
              },
              false,
            );
          }
        }
        for (const prop of boss?.authoredProps ?? v.props ?? [])
          if (prop.vesperCrystal && prop.flash > 0) addPoint(prop.weakpoint, true);
      }
      geometry.setDrawRange(0, vertices);
      particles.setDrawRange(0, particleCount);
      mesh.visible = vertices > 0;
      points.visible = particleCount > 0;
      position.needsUpdate =
        uv.needsUpdate =
        particlePosition.needsUpdate =
        color.needsUpdate =
          true;
    },
    diagnostics: () => ({
      rings: ringCount,
      vertices,
      particles: particleCount,
      ringCapacity: SKY_VESPER.ringLimit,
      particleCapacity: PARTICLES,
    }),
  };
}
